import React, { useCallback, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/Button';
import { SparklesIcon, ChevronRightIcon, ChevronLeftIcon } from 'lucide-react';
const heroImages = [{
  src: 'https://cdn.magicpatterns.com/uploads/ngmwBELr8Q39TBuJBcbaDu/istockphoto-2254725453-612x612.jpg',
  alt: "Cornrow braiding in progress at Beni's salon"
}, {
  src: 'https://cdn.magicpatterns.com/uploads/6t9qKcZmtWQUyXJdcQXU3P/istockphoto-2170405638-612x612.jpg',
  alt: "Beautiful box braids styling at Beni's salon"
}, {
  src: 'https://cdn.magicpatterns.com/uploads/eGLo6DiTb719zndAEqfkfy/istockphoto-1370569973-2048x2048.jpg',
  alt: "Detailed braid work at Beni's African Hair Braiding"
}];
const BLOCK_COUNT = 4;
function CarouselBlocks({
  src,
  alt
}: {
  src: string;
  alt: string;
}) {
  return <>
      {Array.from({
      length: BLOCK_COUNT
    }).map((_, i) => <motion.div key={i} initial={{
      opacity: 0
    }} animate={{
      opacity: 0.5
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.8,
      delay: i * 0.15,
      ease: 'easeInOut'
    }} className="absolute top-0 bottom-0" style={{
      left: `${i / BLOCK_COUNT * 100}%`,
      width: `${100 / BLOCK_COUNT}%`,
      backgroundImage: `url(${src})`,
      backgroundSize: `${BLOCK_COUNT * 100}% 100%`,
      backgroundPosition: `${i / (BLOCK_COUNT - 1) * 100}% center`
    }} role="img" aria-label={i === 0 ? alt : undefined} data-id="element-168" />)}
    </>;
}
const slideVariants = {
  enter: {
    clipPath: 'inset(100% 0 0 0)',
    opacity: 1
  },
  center: {
    clipPath: 'inset(0% 0 0 0)',
    opacity: 0.5
  },
  exit: {
    clipPath: 'inset(0 0 100% 0)',
    opacity: 0
  }
};
export function HeroSection() {
  const navigate = useNavigate();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [resetKey, setResetKey] = useState(0);
  const goToNext = useCallback(() => {
    setCurrentIndex(prev => (prev + 1) % heroImages.length);
    setResetKey(k => k + 1);
  }, []);
  const goToPrev = useCallback(() => {
    setCurrentIndex(prev => (prev - 1 + heroImages.length) % heroImages.length);
    setResetKey(k => k + 1);
  }, []);
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex(prev => (prev + 1) % heroImages.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [resetKey]);
  return <section className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden bg-jet pt-20" data-id="element-169">
      {/* Background Image Carousel */}
      <div className="absolute inset-0 bg-jet" data-id="element-170">
        <AnimatePresence mode="sync" data-id="element-171">
          <CarouselBlocks key={currentIndex} src={heroImages[currentIndex].src} alt={heroImages[currentIndex].alt} data-id="element-172" />
        </AnimatePresence>

        {/* Pattern Overlay */}
        <div className="absolute inset-0 pattern-african opacity-20 mix-blend-overlay" data-id="element-173"></div>

        {/* Decorative Elements */}
        <div className="absolute top-1/4 left-10 w-32 h-32 border border-golden/20 rounded-full blur-sm" data-id="element-174"></div>
        <div className="absolute bottom-1/4 right-10 w-64 h-64 border border-golden/10 rounded-full blur-md" data-id="element-175"></div>
      </div>

      {/* Carousel Navigation Arrows */}
      <button onClick={goToPrev} aria-label="Previous image" className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full border border-white/30 bg-jet/30 backdrop-blur-sm flex items-center justify-center text-white/80 hover:bg-white/15 hover:text-white transition-all" data-id="element-176">
        <ChevronLeftIcon className="w-6 h-6" data-id="element-177" />
      </button>
      <button onClick={goToNext} aria-label="Next image" className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full border border-white/30 bg-jet/30 backdrop-blur-sm flex items-center justify-center text-white/80 hover:bg-white/15 hover:text-white transition-all" data-id="element-178">
        <ChevronRightIcon className="w-6 h-6" data-id="element-179" />
      </button>

      {/* Main Content Area */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center flex-grow flex flex-col justify-center w-full" data-id="element-180">
        <div className="max-w-4xl mx-auto" data-id="element-181">
          <motion.span initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} className="block text-golden font-medium tracking-[0.2em] uppercase mb-4 text-sm md:text-base" data-id="element-182">
            Beni's African Hair Braiding
          </motion.span>

          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="text-5xl md:text-7xl lg:text-8xl font-heading font-bold text-cream mb-6 leading-tight text-glow" data-id="element-183">
            Crown Your <span className="text-golden italic" data-id="element-184">Beauty</span>
          </motion.h1>

          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.2
        }} className="text-lg md:text-xl text-beige/90 mb-10 max-w-2xl mx-auto font-light leading-relaxed" data-id="element-185">
            Experience the art of authentic African braiding in a luxurious,
            welcoming environment. We don't just style hair; we craft
            masterpieces.
          </motion.p>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.3
        }} className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-186">
            <Button variant="primary" size="lg" onClick={() => navigate('/booking')} data-id="element-187">
              Call Now or Book Appointment
            </Button>
            <Button variant="outline" size="lg" className="border-white text-white hover:bg-white/10 hover:border-white hover:text-white" onClick={() => navigate('/services')} data-id="element-188">
              Explore Services
            </Button>
          </motion.div>
        </div>

        {/* Persistent Walk-ins Badge */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.5,
        duration: 0.8
      }} className="mt-12 inline-flex items-center gap-2 px-6 py-2.5 rounded-full border border-golden/30 bg-jet/40 backdrop-blur-md text-beige text-sm font-medium tracking-wide shadow-lg mx-auto" data-id="element-189">
          <SparklesIcon className="w-4 h-4 text-golden" data-id="element-190" />
          <span data-id="element-191">Walk-ins Welcome • No Appointment Needed</span>
        </motion.div>
      </div>

      {/* Bottom decorative divider */}
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-golden to-transparent opacity-50 z-10" data-id="element-192"></div>
    </section>;
}